import { useEffect, useState } from "react";

/**
 * One environment gate for all hub labor surfaces.
 *
 * New setting:
 *   VITE_LABOR_USE_SUPABASE=true
 *
 * Backwards-compatible aliases stay for the transition so existing local
 * `.env` files keep working. TODO(post-labor-refactor): remove the surface-
 * specific aliases after the next major UI bump.
 */
const LABOR_PERSISTENCE_OVERRIDE_STORAGE_KEY = "integrator_hub_labor_persistence_override_v1";
const LABOR_PERSISTENCE_OVERRIDE_CHANGED_EVENT = "integrator-hub-labor-persistence-override-changed";
const LABOR_SHELL_LOCKDOWN_BYPASS_STORAGE_KEY = "integrator_hub_labor_shell_lockdown_bypass_v1";
const LABOR_SHELL_LOCKDOWN_BYPASS_CHANGED_EVENT = "integrator-hub-labor-shell-lockdown-bypass-changed";

export type LaborPersistenceOverride = "default" | "writes-enabled" | "writes-disabled";

function laborEnv(): Partial<ImportMetaEnv> {
  return (import.meta as ImportMeta & { env?: Partial<ImportMetaEnv> }).env ?? {};
}

function dispatchLaborPersistenceOverrideChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(LABOR_PERSISTENCE_OVERRIDE_CHANGED_EVENT));
}

export function readLaborPersistenceOverride(): LaborPersistenceOverride {
  if (!laborEnv().DEV || typeof localStorage === "undefined") {
    return "default";
  }
  try {
    const raw = localStorage.getItem(LABOR_PERSISTENCE_OVERRIDE_STORAGE_KEY);
    return raw === "writes-enabled" || raw === "writes-disabled" ? raw : "default";
  } catch {
    return "default";
  }
}

export function setLaborPersistenceOverride(next: LaborPersistenceOverride): void {
  if (!laborEnv().DEV || typeof localStorage === "undefined") return;
  try {
    if (next === "default") {
      localStorage.removeItem(LABOR_PERSISTENCE_OVERRIDE_STORAGE_KEY);
    } else {
      localStorage.setItem(LABOR_PERSISTENCE_OVERRIDE_STORAGE_KEY, next);
    }
  } finally {
    dispatchLaborPersistenceOverrideChanged();
  }
}

export function subscribeLaborPersistenceOverride(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onChanged = (): void => listener();
  const onStorage = (event: StorageEvent): void => {
    if (event.key !== null && event.key !== LABOR_PERSISTENCE_OVERRIDE_STORAGE_KEY) return;
    listener();
  };
  window.addEventListener(LABOR_PERSISTENCE_OVERRIDE_CHANGED_EVENT, onChanged);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(LABOR_PERSISTENCE_OVERRIDE_CHANGED_EVENT, onChanged);
    window.removeEventListener("storage", onStorage);
  };
}

export function useLaborPersistenceOverride(): LaborPersistenceOverride {
  const [override, setOverride] = useState<LaborPersistenceOverride>(() => readLaborPersistenceOverride());
  useEffect(() => subscribeLaborPersistenceOverride(() => setOverride(readLaborPersistenceOverride())), []);
  return override;
}

export function isLaborShellLockdownBypassed(): boolean {
  if (!laborEnv().DEV || typeof localStorage === "undefined") {
    return false;
  }
  try {
    return localStorage.getItem(LABOR_SHELL_LOCKDOWN_BYPASS_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export function setLaborShellLockdownBypassed(enabled: boolean): void {
  if (!laborEnv().DEV || typeof localStorage === "undefined") return;
  try {
    if (enabled) {
      localStorage.setItem(LABOR_SHELL_LOCKDOWN_BYPASS_STORAGE_KEY, "true");
    } else {
      localStorage.removeItem(LABOR_SHELL_LOCKDOWN_BYPASS_STORAGE_KEY);
    }
  } finally {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(LABOR_SHELL_LOCKDOWN_BYPASS_CHANGED_EVENT));
    }
  }
}

export function subscribeLaborShellLockdownBypass(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onChanged = (): void => listener();
  const onStorage = (event: StorageEvent): void => {
    if (event.key !== null && event.key !== LABOR_SHELL_LOCKDOWN_BYPASS_STORAGE_KEY) return;
    listener();
  };
  window.addEventListener(LABOR_SHELL_LOCKDOWN_BYPASS_CHANGED_EVENT, onChanged);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(LABOR_SHELL_LOCKDOWN_BYPASS_CHANGED_EVENT, onChanged);
    window.removeEventListener("storage", onStorage);
  };
}

export function useLaborShellLockdownBypass(): boolean {
  const [bypassed, setBypassed] = useState(() => isLaborShellLockdownBypassed());
  useEffect(() => subscribeLaborShellLockdownBypass(() => setBypassed(isLaborShellLockdownBypassed())), []);
  return bypassed;
}

export function laborRemotePersistenceDisabled(surface?: "prepack" | "clampqc"): boolean {
  const env = laborEnv();
  if (!env.DEV) {
    return false;
  }
  const override = readLaborPersistenceOverride();
  if (override === "writes-enabled") {
    return false;
  }
  if (override === "writes-disabled") {
    return true;
  }
  if (env.VITE_LABOR_USE_SUPABASE === "true") {
    return false;
  }
  if (surface === "prepack" && env.VITE_PREPACK_USE_SUPABASE_LABOR === "true") {
    return false;
  }
  if (surface === "clampqc" && env.VITE_CLAMP_QC_USE_SUPABASE_LABOR === "true") {
    return false;
  }
  return true;
}

